import React, { useEffect, useMemo, useState } from 'react';
import { FaCoins, FaHistory, FaRedo, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import spinGameService from '../../services/spinGameService';
import { useToast } from '../Shared/Toast';

const formatRs = (value) => `Rs ${Number(value || 0).toFixed(2)}`;
const wheelSegments = [
  { value: 0, label: 'TRY', color: '#334155' },
  { value: 10, label: '10', color: '#ef4444' },
  { value: 20, label: '20', color: '#f97316' },
  { value: 50, label: '50', color: '#facc15' },
  { value: 100, label: '100', color: '#22c55e' },
  { value: 500, label: '500', color: '#06b6d4' },
  { value: 1000, label: '1000', color: '#6366f1' },
];
const winningAmounts = [10, 20, 50, 100, 500, 1000];
const wheelSpinDurationMs = 4600;
const wheelSpinDurationSeconds = wheelSpinDurationMs / 1000;
const getErrorMessage = (err) => {
  const data = err.response?.data;
  if (typeof data === 'string') return data;
  if (data?.message) return data.message;
  if (data?.title) return data.title;
  return err.message || 'Something went wrong.';
};

const SpinGamePage = () => {
  const { success, error } = useToast();
  const [settings, setSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [rotation, setRotation] = useState(0);
  const isFreeSpinAvailable = Number(settings?.freeSpinsRemainingToday || 0) > 0;

  const wheelGradient = useMemo(() => {
    const segment = 100 / wheelSegments.length;
    const stops = wheelSegments.map((segmentItem, index) => {
      const start = (index * segment).toFixed(4);
      const end = ((index + 1) * segment).toFixed(4);
      return `${segmentItem.color} ${start}% ${end}%`;
    });
    return `conic-gradient(from -90deg, ${stops.join(', ')})`;
  }, []);

  const getPrizeAngle = (prizeAmount) => {
    const prizeIndex = wheelSegments.findIndex((segment) => segment.value === Number(prizeAmount));
    if (prizeIndex < 0) return Math.floor(Math.random() * 360);
    const segmentAngle = 360 / wheelSegments.length;
    return prizeIndex * segmentAngle + segmentAngle / 2 - 90;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsData, transactionData] = await Promise.all([
        spinGameService.getSettings(),
        spinGameService.getMyTransactions(),
      ]);
      setSettings(settingsData);
      setTransactions(transactionData);
    } catch (err) {
      error('Spin game load failed', getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSpin = async () => {
    if (!settings?.isActive || spinning) return;

    try {
      setSpinning(true);
      setResult(null);
      const spinResult = await spinGameService.play();
      const targetAngle = getPrizeAngle(spinResult.prizeAmount);
      setRotation((current) => {
        const currentNormalized = ((current % 360) + 360) % 360;
        const finalNormalized = ((360 - targetAngle) % 360 + 360) % 360;
        const delta = (finalNormalized - currentNormalized + 360) % 360;
        return current + 1440 + delta;
      });

      setTimeout(async () => {
        setResult(spinResult);
        window.dispatchEvent(new Event('balanceUpdated'));
        success(
          spinResult.prizeAmount > 0 ? 'You won!' : 'Try again',
          spinResult.prizeAmount > 0
            ? `${formatRs(spinResult.prizeAmount)} added to your wallet.`
            : 'No prize this time.'
        );
        const [settingsData, transactionData] = await Promise.all([
          spinGameService.getSettings(),
          spinGameService.getMyTransactions(),
        ]);
        setSettings(settingsData);
        setTransactions(transactionData);
        setSpinning(false);
      }, wheelSpinDurationMs);
    } catch (err) {
      setSpinning(false);
      error('Spin failed', getErrorMessage(err));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-gray-600">
        <FaSpinner className="mr-2 animate-spin" /> Loading spin game...
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{settings?.name || 'Cash Spin'}</h1>
          <p className="mt-2 text-gray-600">
            {isFreeSpinAvailable ? 'Your daily free spin is ready.' : `Entry ${formatRs(settings?.entryFee)}.`} Win up to {formatRs(1000)}.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col items-center">
            <div className="relative flex h-80 w-80 items-center justify-center">
              <div className="absolute -top-1 z-20 h-0 w-0 border-l-[16px] border-r-[16px] border-t-[30px] border-l-transparent border-r-transparent border-t-gray-900" />
              <motion.div
                animate={{ rotate: rotation }}
                transition={{ duration: wheelSpinDurationSeconds, ease: [0.12, 0.76, 0.16, 1] }}
                className="relative flex h-72 w-72 items-center justify-center rounded-full border-[12px] border-yellow-400 shadow-2xl"
                style={{ background: wheelGradient }}
              >
                <div className="absolute inset-3 rounded-full border-4 border-white/50" />
                {wheelSegments.map((segmentItem, index) => {
                  const segmentAngle = 360 / wheelSegments.length;
                  const angle = index * segmentAngle + segmentAngle / 2 - 90;
                  const isLoseSegment = segmentItem.value === 0;

                  return (
                    <div
                      key={`${segmentItem.value}-${index}`}
                      className={`absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center font-extrabold text-white drop-shadow ${
                        isLoseSegment ? 'h-8 w-20 text-sm' : 'h-9 w-16 text-base'
                      }`}
                      style={{
                        transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-88px) rotate(90deg)`,
                      }}
                    >
                      {segmentItem.label}
                    </div>
                  );
                })}
                <div className="z-10 flex h-28 w-28 items-center justify-center rounded-full bg-white text-center shadow-lg">
                  <div>
                    <FaCoins className="mx-auto mb-2 text-3xl text-yellow-500" />
                    <p className="text-lg font-bold text-gray-900">SPIN</p>
                  </div>
                </div>
              </motion.div>
            </div>

            <button
              type="button"
              onClick={handleSpin}
              disabled={spinning || !settings?.isActive}
              className="mt-8 inline-flex items-center rounded-lg bg-indigo-600 px-8 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {spinning ? <FaSpinner className="mr-2 animate-spin" /> : <FaRedo className="mr-2" />}
              {spinning
                ? 'Spinning...'
                : isFreeSpinAvailable
                  ? 'Daily Free Spin'
                  : `Play for ${formatRs(settings?.entryFee)}`}
            </button>

            {!settings?.isActive && (
              <p className="mt-4 rounded-md bg-yellow-50 px-4 py-2 text-sm text-yellow-700">
                Spin game is currently inactive.
              </p>
            )}

            {result && (
              <div className="mt-6 w-full max-w-md rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                <p className="text-sm text-gray-500">Last result</p>
                {result.prizeAmount > 0 ? (
                  <>
                    <p className="text-3xl font-bold text-gray-900">{formatRs(result.prizeAmount)}</p>
                    <p className="mt-1 text-sm text-gray-600">
                      {result.isFreeSpin ? 'Free spin winning added to your wallet.' : 'Winning amount added to your wallet.'}
                    </p>
                  </>
                ) : (
                  <p className="text-2xl font-bold text-gray-900">Better luck next time</p>
                )}
              </div>
            )}
          </div>
        </section>

        <aside className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Winning Amounts</h2>
            <div className="grid grid-cols-2 gap-3">
              {winningAmounts.map((prize) => (
                <div key={prize} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-center font-bold text-gray-900">
                  {formatRs(prize)}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 flex items-center text-lg font-semibold text-gray-900">
              <FaHistory className="mr-2" /> History
            </h2>
            <div className="max-h-80 space-y-3 overflow-y-auto">
              {transactions.length === 0 ? (
                <p className="text-sm text-gray-500">No spins yet.</p>
              ) : (
                transactions.slice(0, 20).map((transaction) => (
                  <div key={transaction.spinTransactionId} className="flex justify-between rounded-md bg-gray-50 px-3 py-2 text-sm">
                    <div>
                      <p className="font-medium text-gray-800">{transaction.status}</p>
                      <p className="text-xs text-gray-500">
                        {Number(transaction.entryFee || 0) === 0 ? 'Free spin' : new Date(transaction.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <p className="font-semibold text-gray-900">{formatRs(transaction.prizeAmount)}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SpinGamePage;
